/**
 * State -> sales-tax-rate lookup.
 *
 * Pure module: no I/O, no server-only imports, so it can be imported from
 * server routes, client components, AND the Sanity Studio schema (which needs
 * US_STATES for its dropdown options).
 *
 * The rate table itself is owner-editable in Sanity (siteSettings.stateTaxRates)
 * rather than hardcoded here: rates change without a deploy. These are STATE
 * rates only — county and city add-ons are not modelled, so the resolved value
 * is always a prefill the owner can override, never a final authority.
 */

export interface StateTaxRate {
  /** Two-letter upper-case state code, e.g. "CA". */
  state: string;
  /** Sales-tax percentage, e.g. 7.25. */
  rate: number;
}

/** The 50 states plus DC, alphabetical by name so the dropdown needs no sort. */
export const US_STATES: ReadonlyArray<{ code: string; name: string }> = [
  { code: "AL", name: "Alabama" },
  { code: "AK", name: "Alaska" },
  { code: "AZ", name: "Arizona" },
  { code: "AR", name: "Arkansas" },
  { code: "CA", name: "California" },
  { code: "CO", name: "Colorado" },
  { code: "CT", name: "Connecticut" },
  { code: "DE", name: "Delaware" },
  { code: "DC", name: "District of Columbia" },
  { code: "FL", name: "Florida" },
  { code: "GA", name: "Georgia" },
  { code: "HI", name: "Hawaii" },
  { code: "ID", name: "Idaho" },
  { code: "IL", name: "Illinois" },
  { code: "IN", name: "Indiana" },
  { code: "IA", name: "Iowa" },
  { code: "KS", name: "Kansas" },
  { code: "KY", name: "Kentucky" },
  { code: "LA", name: "Louisiana" },
  { code: "ME", name: "Maine" },
  { code: "MD", name: "Maryland" },
  { code: "MA", name: "Massachusetts" },
  { code: "MI", name: "Michigan" },
  { code: "MN", name: "Minnesota" },
  { code: "MS", name: "Mississippi" },
  { code: "MO", name: "Missouri" },
  { code: "MT", name: "Montana" },
  { code: "NE", name: "Nebraska" },
  { code: "NV", name: "Nevada" },
  { code: "NH", name: "New Hampshire" },
  { code: "NJ", name: "New Jersey" },
  { code: "NM", name: "New Mexico" },
  { code: "NY", name: "New York" },
  { code: "NC", name: "North Carolina" },
  { code: "ND", name: "North Dakota" },
  { code: "OH", name: "Ohio" },
  { code: "OK", name: "Oklahoma" },
  { code: "OR", name: "Oregon" },
  { code: "PA", name: "Pennsylvania" },
  { code: "RI", name: "Rhode Island" },
  { code: "SC", name: "South Carolina" },
  { code: "SD", name: "South Dakota" },
  { code: "TN", name: "Tennessee" },
  { code: "TX", name: "Texas" },
  { code: "UT", name: "Utah" },
  { code: "VT", name: "Vermont" },
  { code: "VA", name: "Virginia" },
  { code: "WA", name: "Washington" },
  { code: "WV", name: "West Virginia" },
  { code: "WI", name: "Wisconsin" },
  { code: "WY", name: "Wyoming" },
];

/**
 * Built-in state sales-tax rates, used when siteSettings.stateTaxRates has no
 * row for a state. Ships populated so the feature works without anyone typing
 * 51 rows into the Studio by hand.
 *
 * These are STATE-level base rates as of 2026-08. Two caveats the owner needs
 * to know, both of which is why a Sanity override always wins over this table:
 *
 *   - Local add-ons are NOT included. County and city sales taxes are
 *     substantial in many states (Louisiana and Alabama commonly exceed 9%
 *     combined), so for a shipping address in one of those, the rate below is
 *     an under-estimate. Override per state, or type the rate on the invoice.
 *   - Rates change by legislation and this table does not update itself. When
 *     one goes stale, add a row in Site Settings -> Invoices -> "Sales tax rate
 *     by state (%)" rather than waiting on a deploy.
 *
 * A few entries fold in a mandatory statewide local component because that is
 * what actually gets charged everywhere in the state: UT (4.85 + 1.25) and
 * VA (4.3 + 1.0). AK, DE, MT, NH and OR levy no state sales tax at all — their
 * 0 is a real rate, not a missing value.
 */
export const BUILT_IN_STATE_TAX_RATES: Readonly<Record<string, number>> = {
  AL: 4,      AK: 0,      AZ: 5.6,    AR: 6.5,    CA: 7.25,
  CO: 2.9,    CT: 6.35,   DE: 0,      DC: 6,      FL: 6,
  GA: 4,      HI: 4,      ID: 6,      IL: 6.25,   IN: 7,
  IA: 6,      KS: 6.5,    KY: 6,      LA: 5,      ME: 5.5,
  MD: 6,      MA: 6.25,   MI: 6,      MN: 6.875,  MS: 7,
  MO: 4.225,  MT: 0,      NE: 5.5,    NV: 6.85,   NH: 0,
  NJ: 6.625,  NM: 4.875,  NY: 4,      NC: 4.75,   ND: 5,
  OH: 5.75,   OK: 4.5,    OR: 0,      PA: 6,      RI: 7,
  SC: 6,      SD: 4.2,    TN: 7,      TX: 6.25,   UT: 6.1,
  VT: 6,      VA: 5.3,    WA: 6.5,    WV: 6,      WI: 5,
  WY: 4,
};

/**
 * Coerce the loosely-typed Sanity array into clean rows.
 *
 * A row with a missing or out-of-range rate is DROPPED rather than defaulted to
 * 0: a dropped row falls back to defaultTaxRate, whereas a 0 would silently
 * assert the state is tax-free. A genuine 0 (Oregon) is kept.
 *
 * Later rows win, so an owner correcting a rate by appending a row is not
 * shadowed by the stale one above it.
 */
export function normalizeStateTaxRates(raw: unknown): StateTaxRate[] {
  if (!Array.isArray(raw)) return [];
  const byState = new Map<string, number>();
  for (const row of raw) {
    if (row == null || typeof row !== "object") continue;
    const r = row as { state?: unknown; rate?: unknown };
    const state = String(r.state ?? "").trim().toUpperCase();
    if (!state) continue;
    // Only accept numbers or non-blank strings that parse to numbers; reject
    // booleans, nullish, objects, and whitespace-only strings that coerce to 0.
    let rate: number;
    if (typeof r.rate === "number") {
      if (!Number.isFinite(r.rate)) continue;
      rate = r.rate;
    } else if (typeof r.rate === "string") {
      const trimmed = r.rate.trim();
      if (!trimmed) continue;
      const parsed = Number(trimmed);
      if (!Number.isFinite(parsed)) continue;
      rate = parsed;
    } else {
      continue;
    }
    if (rate < 0 || rate > 100) continue;
    byState.set(state, rate);
  }
  return Array.from(byState, ([state, rate]) => ({ state, rate }));
}

/**
 * Last-resort sales-tax rate when neither a state nor a site default resolves.
 * Shared so the Studio invoice form and /api/checkout/quote cannot disagree —
 * checkout hard-coded 6 while the Studio fell through to null (tax 0), so the
 * same order was taxed differently depending on where it was written up.
 */
export const DEFAULT_TAX_RATE_FALLBACK = 6;

/**
 * The rate to prefill for `state`. Three tiers, most specific first:
 *
 *   1. an owner-entered row in siteSettings.stateTaxRates — always wins, so a
 *      correction never needs a deploy
 *   2. BUILT_IN_STATE_TAX_RATES — so all 51 jurisdictions work out of the box
 *   3. the site-wide defaultTaxRate
 *
 * Returns null when there is no rate to offer at all, so the caller leaves the
 * rate field alone rather than stamping a 0 over a value the owner typed.
 */
export function resolveStateTaxRate(
  state: string | null | undefined,
  table: StateTaxRate[],
  fallback: number | null | undefined
): number | null {
  const code = String(state ?? "").trim().toUpperCase();
  if (code) {
    const hit = table.find((r) => r.state === code);
    if (hit) return hit.rate;
    // Object.hasOwn, not a truthiness check: five states levy no sales tax and
    // their built-in 0 is a real rate that must not fall through to the default.
    if (Object.hasOwn(BUILT_IN_STATE_TAX_RATES, code)) return BUILT_IN_STATE_TAX_RATES[code];
  }
  return typeof fallback === "number" && Number.isFinite(fallback) ? fallback : null;
}
